import { useEffect, useRef, useState } from 'react';

import {
  APPLICATION_FORM_PACKAGE_DOCUMENTS,
  APPLICATION_FORM_PACKAGE_FEATURES,
  APPLICATION_FORM_PACKAGE_SERVICE_ATTRIBUTES,
  APPLICATION_FORM_PACKAGES,
  APPLICATION_FORM_SERVICES,
  COUNTRY,
} from '../../../constants/EntityName';
import { ContentItem, OnAddArguments, TabContent } from '../../../utils/forms/FormInterface';
import { getFormDefaultValue, getTableHeaders } from '../../../utils/forms/FormDataModel';
import GenericAccordian from '../../../components/GenericAccordian/GenericAccordian';
import AbstractForm from '../../Entity/AbstractForm';
import AbstractTabs, { TabActiosEventType, TabActiosProps } from '../../Entity/AbstractTabs';
import AbstractLanding from '../../Entity/AbstractLanding';
import { PackagesFormData } from '../../../datamodels/ApplicationFormMaster/FormPackages';

const additionalEntityName = [
  { name: APPLICATION_FORM_SERVICES, getAllResponse: true },
  { name: APPLICATION_FORM_PACKAGE_SERVICE_ATTRIBUTES, getAllResponse: true },
  { name: APPLICATION_FORM_PACKAGE_DOCUMENTS, getAllResponse: true },
  { name: APPLICATION_FORM_PACKAGE_FEATURES, getAllResponse: true },
  { name: COUNTRY, getAllResponse: true },
];

const ApplicationFormPackagesPage = () => {
  const [activeKey, setActiveKey] = useState<string>('0');
  const [userName, setUserName] = useState<string>();
  const [showFormComponent, setShowFormComponent] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [content, setContent] = useState<TabContent>([]);
  const deepLinkConsumedRef = useRef(false);

  const defaultFormValue: any = getFormDefaultValue(PackagesFormData);
  const tableHeaders: any = getTableHeaders(PackagesFormData);
  const addName = 'Form Package';
  const headerName = 'Form Package';

  const onSuccessful = (id?: string) => {
    if (id === 'create') {
      setShowFormComponent(false);
    }
    onTabClick('0');
    setRefreshData(prev => !prev);
  };

  const onAddNew = (argument1?: OnAddArguments) => {
    if (argument1) {
      const alreadyOpen = content.find((item: ContentItem) => item.key === argument1.id);
      if (alreadyOpen) {
        // Tab already exists — just switch to it
        onTabClick(String(argument1.id));
      } else {
        onAdd(argument1);
      }
    } else {
      setShowFormComponent(true);
      setUserName('Add Form Package');
      onTabClick('a');
    }
  };

  useEffect(() => {
    if (deepLinkConsumedRef.current) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const recordId = params.get('cmsRecordId');
    if (!recordId) {
      return;
    }
    deepLinkConsumedRef.current = true;
    const recordName = params.get('cmsRecordName') || recordId;
    onAddNew({ id: recordId, name: recordName });
    window.history.replaceState({}, document.title, window.location.pathname);
    // Deep-link open once on mount (new tab from list).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeTab = (key: string, shouldRefresh: boolean) => {
    setContent(content => content.filter(item => item.key !== key));
    setActiveKey('0');
    shouldRefresh && setRefreshData(prev => !prev);
    key === 'a' && setShowFormComponent(false);
  };

  const onAdd = (argument1: OnAddArguments) => {
    const newObj = {
      key: argument1.id,
      tabTitle: argument1.name || argument1.id,
      content: (
        <GenericAccordian key={content.length}>
          <AbstractForm
            entityId={argument1.id}
            isCreateMode={false}
            headerName={headerName}
            entityName={APPLICATION_FORM_PACKAGES}
            formTemplate={PackagesFormData}
            formDefaultPayload={getFormDefaultValue(PackagesFormData)}
            onSuccessfulUpdate={onSuccessful}
            formHeader=""
            showDeleteDraft={true}
            closeTab={closeTab}
            additionalEntityName={additionalEntityName}
          />
        </GenericAccordian>
      ),
    };

    // Below line of code is for show tab index when we select tab
    setContent((prev: any) => [...prev, newObj]);
    argument1.id && setActiveKey(String(argument1.id));
  };

  const onTabClick = (key: string) => {
    setActiveKey(key);
  };

  const updateFormVisibility = (
    action: TabActiosProps,
    targetKey?: string | TabActiosEventType
  ) => {
    if (action === 'remove') {
      if (targetKey === 'a') {
        setShowFormComponent(false);
        onTabClick('0');
      } else {
        onTabClick('0');
        // whenever a tab is closed, that tab is filtered out from the list of tabs which are rendered inside the AbstractTab component
        setContent(content => content.filter(item => String(item.key) !== targetKey));
      }
    }
  };

  return (
    <AbstractTabs
      activeKey={activeKey}
      entityName="Form Package"
      updateFormVisibility={updateFormVisibility}
      onTabClick={onTabClick}
      secondTabTitle={userName}
      showFormComponent={showFormComponent}
      content={content}
      formComponent={
        <GenericAccordian>
          <AbstractForm
            isCreateMode={true}
            headerName={headerName}
            entityName={APPLICATION_FORM_PACKAGES}
            formTemplate={PackagesFormData}
            formDefaultPayload={defaultFormValue}
            onSuccessfulUpdate={onSuccessful}
            closeTab={closeTab}
            additionalEntityName={additionalEntityName}
          />
        </GenericAccordian>
      }
    >
      <AbstractLanding
        headerName={headerName}
        addName={addName}
        entityName={APPLICATION_FORM_PACKAGES}
        tableHeaders={tableHeaders}
        onAdd={onAddNew}
        refreshData={refreshData}
        showFilter={true}
      />
    </AbstractTabs>
  );
};

export default ApplicationFormPackagesPage;
